import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { PublicLayout } from './PublicLayout';
import {
  adminApi,
  trackerApi,
  type AdminAppControlResponse,
  type SupportRequestApiItem,
  type AdminUserApiItem,
  type AdminUserUpdatePayload,
  type LiveWsStatusResponse,
  type SchedulerStatusResponse,
  type StoredSurebetCandidateApiItem,
  type StoredSurebetsResponse,
  type SurebetHistoryApiItem,
} from './api';

type AdminTab = 'overzicht' | 'users' | 'support' | 'surebet-history' | 'system';
type TelegramChannel = 'prematch' | 'live';
type StoredTypeFilter = 'all' | 'live' | 'prematch';
type StoredSortOption = 'sent-desc' | 'sent-asc' | 'roi-desc' | 'roi-asc' | 'type-asc' | 'status-asc';
type SupportRequestStatusFilter = 'all' | 'new' | 'in_progress' | 'closed';
type AdminUserPlanFilter = 'all' | 'active' | 'stripe' | 'manual' | 'none' | 'pro' | 'pre-match' | 'live';
type AdminUserTelegramFilter = 'all' | 'linked' | 'unlinked' | 'attention';
type AdminUserSort = 'newest' | 'last-login' | 'name' | 'subscription-end';
type AdminUserDraft = Required<Pick<AdminUserUpdatePayload, 'role' | 'plan' | 'subscriptionStatus'>> & {
  subscriptionEndsAt: string;
};

const ADMIN_HISTORY_PAGE_SIZE = 10;
const ADMIN_USERS_PAGE_SIZE = 12;

interface ControlDraft {
  enabled: boolean;
  prematchEnabled: boolean;
  liveEnabled: boolean;
  prematchTelegramSendEnabled: boolean;
  liveTelegramSendEnabled: boolean;
  minRoiPercent: string;
}

const ADMIN_TABS: Array<{ key: AdminTab; label: string }> = [
  { key: 'overzicht', label: 'Dashboard' },
  { key: 'users', label: 'Gebruikers' },
  { key: 'support', label: 'Support' },
  { key: 'surebet-history', label: 'Surebets' },
  { key: 'system', label: 'Systeem' },
];

const SUPPORT_REQUEST_STATUS_OPTIONS: Array<{ value: Exclude<SupportRequestStatusFilter, 'all'>; label: string }> = [
  { value: 'new', label: 'Nieuw' },
  { value: 'in_progress', label: 'In behandeling' },
  { value: 'closed', label: 'Afgerond' },
];

const ADMIN_USER_ROLE_OPTIONS: Array<{ value: AdminUserDraft['role']; label: string }> = [
  { value: 'user', label: 'Gebruiker' },
  { value: 'admin', label: 'Admin' },
];

const ADMIN_USER_PLAN_OPTIONS: Array<{ value: AdminUserDraft['plan']; label: string }> = [
  { value: 'none', label: 'Geen abonnement' },
  { value: 'pro', label: 'Pro' },
  { value: 'pre-match', label: 'Pre-match' },
  { value: 'live', label: 'Live' },
];

const ADMIN_USER_STATUS_OPTIONS: Array<{ value: AdminUserDraft['subscriptionStatus']; label: string }> = [
  { value: 'none', label: 'Geen status' },
  { value: 'active', label: 'Actief' },
  { value: 'trialing', label: 'Trial' },
  { value: 'past_due', label: 'Betaling mislukt' },
  { value: 'canceled', label: 'Geannuleerd' },
  { value: 'expired', label: 'Verlopen' },
];

const emptyControlDraft: ControlDraft = {
  enabled: false,
  prematchEnabled: false,
  liveEnabled: false,
  prematchTelegramSendEnabled: false,
  liveTelegramSendEnabled: false,
  minRoiPercent: '4.0',
};

const formatDateTime = (value?: string | null) => {
  if (!value) return 'Nog niet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('nl-NL', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(date);
};

const formatPercent = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return `${value.toFixed(2)}%`;
};

const formatMoney = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '-';
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

const formatDurationSeconds = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return 'Nog niet';
  if (value < 1) return `${Math.round(value * 1000)} ms`;
  return `${value.toFixed(1)} s`;
};

const getInitials = (name: string) => name
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((part) => part.charAt(0).toUpperCase())
  .join('') || '?';

const getPlanLabel = (value: string) => ADMIN_USER_PLAN_OPTIONS.find((option) => option.value === value)?.label || value || 'Geen abonnement';
const getSubscriptionStatusLabel = (value: string) => ADMIN_USER_STATUS_OPTIONS.find((option) => option.value === value)?.label || value || 'Geen status';

const getBillingCycleLabel = (value?: string | null) => {
  if (value === 'month') return 'Maandelijks';
  if (value === 'week') return 'Wekelijks';
  if (value === 'day-pass') return 'Dagpas';
  return 'Geen looptijd';
};

const getTelegramStatusLabel = (value?: string | null) => {
  if (value === 'joined') return 'Lid';
  if (value === 'invited') return 'Uitgenodigd';
  if (value === 'error') return 'Actie nodig';
  if (value === 'removed') return 'Verwijderd';
  if (value === 'not_synced') return 'Niet gesynchroniseerd';
  return value || 'Niet gekoppeld';
};

const getErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (detail && typeof detail === 'object' && 'detail' in detail) {
      const message = (detail as { detail?: unknown }).detail;
      if (typeof message === 'string' && message.trim()) {
        return message;
      }
    }
    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return 'Onbekende fout.';
};

const getBadgeClass = (variant: 'success' | 'warning' | 'danger' | 'soft') => `badge badge-${variant}`;

const getSupportRequestStatusLabel = (value: string) => {
  if (value === 'new') return 'Nieuw';
  if (value === 'in_progress') return 'In behandeling';
  if (value === 'closed') return 'Afgerond';
  return value || 'Onbekend';
};

const getSupportRequestStatusVariant = (value: string): 'success' | 'warning' | 'danger' | 'soft' => {
  if (value === 'closed') return 'success';
  if (value === 'new') return 'warning';
  if (value === 'in_progress') return 'soft';
  return 'soft';
};

const getSupportMailStatusLabel = (value: string) => {
  if (value === 'sent') return 'Verzonden';
  if (value === 'failed') return 'Mislukt';
  if (value === 'not_configured') return 'Niet geconfigureerd';
  if (value === 'pending') return 'In wachtrij';
  return value || 'Onbekend';
};

const getSupportMailStatusVariant = (value: string): 'success' | 'warning' | 'danger' | 'soft' => {
  if (value === 'sent') return 'success';
  if (value === 'failed') return 'danger';
  if (value === 'not_configured') return 'warning';
  return 'soft';
};

const renderBooleanBadge = (value: boolean, trueLabel = 'Actief', falseLabel = 'Uit') => (
  <span className={getBadgeClass(value ? 'success' : 'soft')}>{value ? trueLabel : falseLabel}</span>
);

const getStoredCandidateTypeKey = (candidate: StoredSurebetCandidateApiItem): Exclude<StoredTypeFilter, 'all'> => (
  candidate.candidateType.toLowerCase().includes('live') ? 'live' : 'prematch'
);

const getDateTimeValue = (value?: string | null) => {
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
};

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] || '';
};

const getTodayDateInputValue = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const toControlDraft = (control: AdminAppControlResponse): ControlDraft => ({
  enabled: control.appEnabled,
  prematchEnabled: control.prematchEnabled,
  liveEnabled: control.liveEnabled,
  prematchTelegramSendEnabled: control.prematchTelegramSendEnabled,
  liveTelegramSendEnabled: control.liveTelegramSendEnabled,
  minRoiPercent: String(control.minRoiPercent ?? 0),
});

const toAdminUserDraft = (user: AdminUserApiItem): AdminUserDraft => ({
  role: user.role === 'admin' ? 'admin' : 'user',
  plan: user.plan === 'pro' || user.plan === 'pre-match' || user.plan === 'live' ? user.plan : 'none',
  subscriptionStatus: user.subscriptionStatus === 'active'
    || user.subscriptionStatus === 'trialing'
    || user.subscriptionStatus === 'past_due'
    || user.subscriptionStatus === 'canceled'
    || user.subscriptionStatus === 'expired'
    ? user.subscriptionStatus
    : 'none',
  subscriptionEndsAt: toDateInputValue(user.subscriptionEndsAt),
});

export const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('overzicht');
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [sendingChannel, setSendingChannel] = useState<TelegramChannel | null>(null);
  const [search, setSearch] = useState('');
  const [storedMinRoi, setStoredMinRoi] = useState('');
  const [storedTypeFilter, setStoredTypeFilter] = useState<StoredTypeFilter>('all');
  const [storedStatusFilter, setStoredStatusFilter] = useState('all');
  const [storedSort, setStoredSort] = useState<StoredSortOption>('sent-desc');
  const [storedPage, setStoredPage] = useState(1);
  const [trackerHistoryPage, setTrackerHistoryPage] = useState(1);
  const [selectedStoredIds, setSelectedStoredIds] = useState<number[]>([]);
  const [selectedTrackerHistoryIds, setSelectedTrackerHistoryIds] = useState<number[]>([]);
  const [isDeletingStored, setIsDeletingStored] = useState(false);
  const [isDeletingTrackerHistory, setIsDeletingTrackerHistory] = useState(false);
  const [appControl, setAppControl] = useState<AdminAppControlResponse | null>(null);
  const [controlDraft, setControlDraft] = useState<ControlDraft>(emptyControlDraft);
  const [schedulerStatus, setSchedulerStatus] = useState<SchedulerStatusResponse | null>(null);
  const [liveWsStatus, setLiveWsStatus] = useState<LiveWsStatusResponse | null>(null);
  const [supportRequests, setSupportRequests] = useState<SupportRequestApiItem[]>([]);
  const [supportRequestDrafts, setSupportRequestDrafts] = useState<Record<number, string>>({});
  const [supportStatusFilter, setSupportStatusFilter] = useState<SupportRequestStatusFilter>('all');
  const [supportPage, setSupportPage] = useState(1);
  const [savingSupportRequestId, setSavingSupportRequestId] = useState<number | null>(null);
  const [adminUsers, setAdminUsers] = useState<AdminUserApiItem[]>([]);
  const [adminUserDrafts, setAdminUserDrafts] = useState<Record<number, AdminUserDraft>>({});
  const [userSearch, setUserSearch] = useState('');
  const [userPlanFilter, setUserPlanFilter] = useState<AdminUserPlanFilter>('all');
  const [userTelegramFilter, setUserTelegramFilter] = useState<AdminUserTelegramFilter>('all');
  const [userSort, setUserSort] = useState<AdminUserSort>('newest');
  const [userPage, setUserPage] = useState(1);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [savingUserId, setSavingUserId] = useState<number | null>(null);
  const [sendingUserInvitesId, setSendingUserInvitesId] = useState<number | null>(null);
  const [confirmDeleteUserId, setConfirmDeleteUserId] = useState<number | null>(null);
  const [deletingUserId, setDeletingUserId] = useState<number | null>(null);
  const [storedSurebets, setStoredSurebets] = useState<StoredSurebetsResponse | null>(null);
  const [trackerHistory, setTrackerHistory] = useState<SurebetHistoryApiItem[]>([]);

  const showNotice = useCallback((message: string) => {
    setNotice(message);
    window.setTimeout(() => {
      setNotice((current) => (current === message ? '' : current));
    }, 3200);
  }, []);

  const refreshDashboard = useCallback(async (options?: { silent?: boolean; searchTerm?: string; minRoiTerm?: string }) => {
    const { silent = false, searchTerm = '', minRoiTerm = '' } = options ?? {};
    setError('');
    if (silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    const parsedStoredMinRoi = minRoiTerm.trim() ? Number(minRoiTerm.replace(',', '.')) : undefined;
    const storedParams = {
      limit: 250,
      include_sent: true,
      search: searchTerm.trim() || undefined,
      min_roi: typeof parsedStoredMinRoi === 'number' && Number.isFinite(parsedStoredMinRoi) ? parsedStoredMinRoi : undefined,
    };

    const [appResult, schedulerResult, liveWsResult, usersResult, supportResult, storedResult, historyResult] = await Promise.allSettled([
      adminApi.getAppControl(),
      adminApi.getSchedulerStatus(),
      adminApi.getLiveWsStatus(),
      adminApi.getUsers(),
      adminApi.getSupportRequests(),
      adminApi.getStoredSurebets(storedParams),
      trackerApi.getTrackerHistory({ limit: 250, includeCurrentDay: true }),
    ]);

    if (appResult.status === 'fulfilled') {
      setAppControl(appResult.value.data);
      setControlDraft(toControlDraft(appResult.value.data));
    } else {
      setError(getErrorMessage(appResult.reason));
    }

    setSchedulerStatus(schedulerResult.status === 'fulfilled' ? schedulerResult.value.data : null);
    setLiveWsStatus(liveWsResult.status === 'fulfilled' ? liveWsResult.value.data : null);
    if (usersResult.status === 'fulfilled') {
      const users = usersResult.value.data.users ?? [];
      setAdminUsers(users);
      setAdminUserDrafts(Object.fromEntries(users.map((user) => [user.id, toAdminUserDraft(user)])));
    } else {
      setAdminUsers([]);
      setAdminUserDrafts({});
    }
    if (supportResult.status === 'fulfilled') {
      const requests = supportResult.value.data.requests ?? [];
      setSupportRequests(requests);
      setSupportRequestDrafts(Object.fromEntries(requests.map((requestItem) => [requestItem.id, requestItem.status])));
    } else {
      setSupportRequests([]);
      setSupportRequestDrafts({});
    }
    setStoredSurebets(storedResult.status === 'fulfilled' ? storedResult.value.data : null);
    setTrackerHistory(historyResult.status === 'fulfilled' ? historyResult.value.data : []);
    setSupportPage(1);
    setStoredPage(1);
    setTrackerHistoryPage(1);
    setSelectedStoredIds([]);
    setSelectedTrackerHistoryIds([]);

    if (schedulerResult.status === 'rejected' && appResult.status === 'fulfilled') {
      showNotice(`Scheduler status niet volledig geladen: ${getErrorMessage(schedulerResult.reason)}`);
    }
    if (liveWsResult.status === 'rejected' && appResult.status === 'fulfilled') {
      showNotice(`Live WS status niet volledig geladen: ${getErrorMessage(liveWsResult.reason)}`);
    }
    if (usersResult.status === 'rejected' && appResult.status === 'fulfilled') {
      showNotice(`Gebruikers niet geladen: ${getErrorMessage(usersResult.reason)}`);
    }
    if (supportResult.status === 'rejected' && appResult.status === 'fulfilled') {
      showNotice(`Klantsupport niet geladen: ${getErrorMessage(supportResult.reason)}`);
    }

    setIsLoading(false);
    setIsRefreshing(false);
  }, [showNotice]);

  useEffect(() => {
    void refreshDashboard();
  }, [refreshDashboard]);

  const handleSaveControls = async () => {
    const parsedMinRoi = Number(controlDraft.minRoiPercent.replace(',', '.'));
    if (!Number.isFinite(parsedMinRoi) || parsedMinRoi < 0) {
      setError('Min ROI moet een geldig positief getal zijn.');
      return;
    }

    setIsSaving(true);
    setError('');
    try {
      const response = await adminApi.updateAppControl({
        enabled: controlDraft.enabled,
        prematchEnabled: controlDraft.prematchEnabled,
        liveEnabled: controlDraft.liveEnabled,
        prematchTelegramSendEnabled: controlDraft.prematchTelegramSendEnabled,
        liveTelegramSendEnabled: controlDraft.liveTelegramSendEnabled,
        minRoiPercent: parsedMinRoi,
      });
      setAppControl(response.data);
      setControlDraft(toControlDraft(response.data));
      showNotice('Runtime instellingen bijgewerkt.');
      await refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi });
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsSaving(false);
    }
  };

  const handleTelegramTest = async (channel: TelegramChannel) => {
    setSendingChannel(channel);
    setError('');
    try {
      const response = await adminApi.sendTelegramTest(channel);
      showNotice(`Telegram ${response.data.channel} test verstuurd voor ${response.data.match}.`);
      await refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi });
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSendingChannel(null);
    }
  };

  const getTrackerHistoryNumericId = (id: string) => {
    const parsedId = Number(id);
    return Number.isInteger(parsedId) && parsedId > 0 ? parsedId : null;
  };

  const handleDeleteStored = async () => {
    if (selectedStoredIds.length === 0) {
      showNotice('Selecteer eerst stored surebets om te verwijderen.');
      return;
    }
    setIsDeletingStored(true);
    setError('');
    try {
      const response = await adminApi.deleteStoredSurebets(selectedStoredIds);
      showNotice(`${response.data.deleted} stored surebet${response.data.deleted === 1 ? '' : 's'} verwijderd.`);
      await refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi });
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsDeletingStored(false);
    }
  };

  const handleDeleteTrackerHistory = async () => {
    if (selectedTrackerHistoryIds.length === 0) {
      showNotice('Selecteer eerst verzonden surebets om te verwijderen.');
      return;
    }
    setIsDeletingTrackerHistory(true);
    setError('');
    try {
      const response = await adminApi.deleteTrackerHistory(selectedTrackerHistoryIds);
      showNotice(`${response.data.deleted} tracker history item${response.data.deleted === 1 ? '' : 's'} verwijderd.`);
      await refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi });
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setIsDeletingTrackerHistory(false);
    }
  };

  const handleSaveUser = async (userId: number) => {
    const draft = adminUserDrafts[userId];
    if (!draft) return;
    const currentUser = adminUsers.find((user) => user.id === userId);
    if (!currentUser) return;
    const original = toAdminUserDraft(currentUser);
    const payload: AdminUserUpdatePayload = {
      role: draft.role,
      plan: draft.plan,
      subscriptionStatus: draft.subscriptionStatus,
      ...(draft.subscriptionEndsAt !== original.subscriptionEndsAt
        ? { subscriptionEndsAt: draft.subscriptionEndsAt || null }
        : {}),
    };

    setSavingUserId(userId);
    setError('');
    try {
      const response = await adminApi.updateUser(userId, payload);
      setAdminUsers((current) => current.map((user) => (user.id === userId ? { ...user, ...response.data.user } : user)));
      setAdminUserDrafts((current) => ({ ...current, [userId]: toAdminUserDraft(response.data.user) }));
      const syncError = response.data.telegramSync?.status === 'error' ? ` Telegram sync: ${response.data.telegramSync.error}` : '';
      showNotice(`Gebruiker bijgewerkt.${syncError}`);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSavingUserId(null);
    }
  };

  const handleSendUserTelegramInvites = async (userId: number) => {
    setSendingUserInvitesId(userId);
    setError('');
    try {
      const response = await adminApi.sendUserTelegramInvites(userId);
      setAdminUsers((current) => current.map((user) => (user.id === userId ? { ...user, ...response.data.user } : user)));
      setAdminUserDrafts((current) => ({ ...current, [userId]: toAdminUserDraft(response.data.user) }));
      showNotice(response.data.message);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSendingUserInvitesId(null);
    }
  };

  const handleSaveSupportRequest = async (requestId: number) => {
    const nextStatus = supportRequestDrafts[requestId];
    if (!nextStatus) return;

    setSavingSupportRequestId(requestId);
    setError('');
    try {
      const response = await adminApi.updateSupportRequest(requestId, { status: nextStatus });
      setSupportRequests((current) => current.map((item) => (item.id === requestId ? response.data.request : item)));
      setSupportRequestDrafts((current) => ({ ...current, [requestId]: response.data.request.status }));
      showNotice('Supportvraag bijgewerkt.');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setSavingSupportRequestId(null);
    }
  };

  const handleDeleteUser = async (userId: number) => {
    setDeletingUserId(userId);
    setError('');
    try {
      await adminApi.deleteUser(userId);
      setAdminUsers((current) => current.filter((user) => user.id !== userId));
      setAdminUserDrafts((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });
      setConfirmDeleteUserId((current) => (current === userId ? null : current));
      setSelectedUserId((current) => (current === userId ? null : current));
      showNotice('Gebruiker verwijderd.');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setDeletingUserId(null);
    }
  };

  const handleCopy = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      showNotice(`${label} gekopieerd.`);
    } catch {
      setError(`${label} kon niet worden gekopieerd.`);
    }
  };

  const supportSummary = useMemo(() => ({
    total: supportRequests.length,
    newCount: supportRequests.filter((item) => item.status === 'new').length,
    inProgressCount: supportRequests.filter((item) => item.status === 'in_progress').length,
    closedCount: supportRequests.filter((item) => item.status === 'closed').length,
  }), [supportRequests]);
  const filteredSupportRequests = useMemo(() => supportRequests.filter((item) => (
    supportStatusFilter === 'all' ? true : item.status === supportStatusFilter
  )), [supportRequests, supportStatusFilter]);
  const supportPageCount = Math.max(1, Math.ceil(filteredSupportRequests.length / ADMIN_HISTORY_PAGE_SIZE));
  const visibleSupportRequests = filteredSupportRequests.slice((supportPage - 1) * ADMIN_HISTORY_PAGE_SIZE, supportPage * ADMIN_HISTORY_PAGE_SIZE);
  const userSummary = useMemo(() => {
    const hasActiveAccess = (user: AdminUserApiItem) => (
      (user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing') && user.plan !== 'none'
    );
    return {
      total: adminUsers.length,
      active: adminUsers.filter(hasActiveAccess).length,
      stripe: adminUsers.filter((user) => user.billingManagedByStripe).length,
      manual: adminUsers.filter((user) => hasActiveAccess(user) && !user.billingManagedByStripe).length,
      telegramLinked: adminUsers.filter((user) => user.telegramLinked).length,
      attention: adminUsers.filter((user) => (
        user.subscriptionStatus === 'past_due'
        || user.telegramAccessStatus === 'error'
        || (hasActiveAccess(user) && !user.telegramLinked)
      )).length,
      neverLoggedIn: adminUsers.filter((user) => !user.lastLoginAt).length,
      stripeRevenue: adminUsers.reduce((sum, user) => sum + (user.stripePaidTotal ?? 0), 0),
    };
  }, [adminUsers]);
  const filteredAdminUsers = useMemo(() => {
    const normalizedSearch = userSearch.trim().toLocaleLowerCase('nl-NL');
    const filtered = adminUsers.filter((user) => {
      const matchesSearch = !normalizedSearch || [
        user.name,
        user.email,
        String(user.id),
        user.telegramUsername,
        user.telegramChatId,
        user.stripeCustomerId,
      ].some((value) => String(value || '').toLocaleLowerCase('nl-NL').includes(normalizedSearch));
      if (!matchesSearch) return false;

      const hasActiveAccess = (user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing') && user.plan !== 'none';
      if (userPlanFilter === 'active' && !hasActiveAccess) return false;
      if (userPlanFilter === 'stripe' && !user.billingManagedByStripe) return false;
      if (userPlanFilter === 'manual' && !(hasActiveAccess && !user.billingManagedByStripe)) return false;
      if (userPlanFilter === 'none' && user.plan !== 'none') return false;
      if (!['all', 'active', 'stripe', 'manual', 'none'].includes(userPlanFilter) && user.plan !== userPlanFilter) return false;

      if (userTelegramFilter === 'linked' && !user.telegramLinked) return false;
      if (userTelegramFilter === 'unlinked' && user.telegramLinked) return false;
      if (userTelegramFilter === 'attention' && !(
        user.telegramAccessStatus === 'error' || (hasActiveAccess && !user.telegramLinked)
      )) return false;
      return true;
    });

    return [...filtered].sort((a, b) => {
      if (userSort === 'name') return a.name.localeCompare(b.name, 'nl');
      if (userSort === 'last-login') return getDateTimeValue(b.lastLoginAt) - getDateTimeValue(a.lastLoginAt);
      if (userSort === 'subscription-end') return getDateTimeValue(a.subscriptionEndsAt) - getDateTimeValue(b.subscriptionEndsAt);
      return getDateTimeValue(b.createdAt) - getDateTimeValue(a.createdAt);
    });
  }, [adminUsers, userPlanFilter, userSearch, userSort, userTelegramFilter]);
  const userPageCount = Math.max(1, Math.ceil(filteredAdminUsers.length / ADMIN_USERS_PAGE_SIZE));
  const visibleAdminUsers = filteredAdminUsers.slice((userPage - 1) * ADMIN_USERS_PAGE_SIZE, userPage * ADMIN_USERS_PAGE_SIZE);
  const selectedUser = selectedUserId === null ? null : adminUsers.find((user) => user.id === selectedUserId) ?? null;
  const storedSummary = storedSurebets?.summary;
  const storedCandidates = useMemo(() => storedSurebets?.candidates ?? [], [storedSurebets]);
  const storedStatuses = useMemo(() => Array.from(new Set(storedCandidates.map((candidate) => candidate.status).filter(Boolean))).sort(), [storedCandidates]);
  const filteredStoredCandidates = useMemo(() => {
    const candidates = storedCandidates.filter((candidate) => {
      if (storedTypeFilter !== 'all' && getStoredCandidateTypeKey(candidate) !== storedTypeFilter) return false;
      if (storedStatusFilter !== 'all' && candidate.status !== storedStatusFilter) return false;
      return true;
    });

    return [...candidates].sort((a, b) => {
      if (storedSort === 'roi-desc') return b.roiPercent - a.roiPercent;
      if (storedSort === 'roi-asc') return a.roiPercent - b.roiPercent;
      if (storedSort === 'sent-asc') return getDateTimeValue(a.sentAt) - getDateTimeValue(b.sentAt);
      if (storedSort === 'type-asc') return a.candidateType.localeCompare(b.candidateType, 'nl');
      if (storedSort === 'status-asc') return a.status.localeCompare(b.status, 'nl');
      return getDateTimeValue(b.sentAt) - getDateTimeValue(a.sentAt);
    });
  }, [storedCandidates, storedSort, storedStatusFilter, storedTypeFilter]);
  const storedPageCount = Math.max(1, Math.ceil(filteredStoredCandidates.length / ADMIN_HISTORY_PAGE_SIZE));
  const visibleStoredCandidates = filteredStoredCandidates.slice((storedPage - 1) * ADMIN_HISTORY_PAGE_SIZE, storedPage * ADMIN_HISTORY_PAGE_SIZE);
  const visibleStoredIds = visibleStoredCandidates.map((candidate) => candidate.id);
  const allVisibleStoredSelected = visibleStoredIds.length > 0 && visibleStoredIds.every((id) => selectedStoredIds.includes(id));
  const trackerHistoryPageCount = Math.max(1, Math.ceil(trackerHistory.length / ADMIN_HISTORY_PAGE_SIZE));
  const visibleTrackerHistory = trackerHistory.slice((trackerHistoryPage - 1) * ADMIN_HISTORY_PAGE_SIZE, trackerHistoryPage * ADMIN_HISTORY_PAGE_SIZE);
  const visibleTrackerHistoryIds = visibleTrackerHistory
    .map((item) => getTrackerHistoryNumericId(item.id))
    .filter((id): id is number => id !== null);
  const allVisibleTrackerHistorySelected = visibleTrackerHistoryIds.length > 0 && visibleTrackerHistoryIds.every((id) => selectedTrackerHistoryIds.includes(id));
  const liveWsConnections = liveWsStatus ? Object.entries(liveWsStatus.connections || {}) : [];

  useEffect(() => {
    setUserPage((current) => Math.min(current, userPageCount));
  }, [userPageCount]);
  return (
    <PublicLayout>
      <section className="public-page-card admin-page">
        <section className="admin-hero">
          <div className="admin-hero-main">
            <span className="admin-hero-eyebrow"><i aria-hidden="true" /> Beveiligde beheeromgeving</span>
            <h1>Beheercentrum</h1>
            <p>Gebruikers, klantvragen, surebets en systeembesturing op één rustige plek.</p>
          </div>
          <div className="admin-hero-state">
            <span className={appControl?.effectiveEnabled ? 'is-online' : 'is-offline'}>
              <i aria-hidden="true" /> {appControl?.effectiveEnabled ? 'Systemen online' : 'Aandacht vereist'}
            </span>
            <small>Omgeving</small>
            <strong>{appControl?.environment || 'laden...'}</strong>
          </div>
        </section>

        {notice && <div className="admin-notice" role="status">{notice}</div>}
        {error && <div className="admin-alert admin-alert-danger" role="alert">{error}</div>}
        {activeTab === 'overzicht' && <div className="admin-stats-grid">
          <div className="admin-stat-card card">
            <div className="stat-value">{userSummary.total}</div>
            <div className="stat-label">Gebruikers</div>
            <div className="stat-subtext">{userSummary.active} met actieve toegang</div>
          </div>
          <div className="admin-stat-card card">
            <div className="stat-value">{formatMoney(userSummary.stripeRevenue)}</div>
            <div className="stat-label">Stripe-omzet</div>
            <div className="stat-subtext">Op basis van verwerkte betalingen</div>
          </div>
          <div className="admin-stat-card card">
            <div className="stat-value">{userSummary.telegramLinked}</div>
            <div className="stat-label">Telegram gekoppeld</div>
            <div className="stat-subtext">{Math.max(0, userSummary.active - userSummary.telegramLinked)} actieve accounts nog niet gekoppeld</div>
          </div>
          <div className="admin-stat-card card">
            <div className="stat-value">{supportSummary.newCount}</div>
            <div className="stat-label">Nieuwe supportvragen</div>
            <div className="stat-subtext">{supportSummary.inProgressCount} in behandeling</div>
          </div>
        </div>}

        <div className="admin-tabs-container">
          <div className="admin-tabs">
            {ADMIN_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`admin-tab ${activeTab === tab.key ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.key)}
                type="button"
              >
                {tab.label}
              </button>
            ))}
            <button
              className="card-btn card-btn-sm"
              disabled={isRefreshing || isLoading}
              aria-busy={isRefreshing || isLoading}
              onClick={() => { void refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi }); }}
              type="button"
            >
              {isRefreshing ? 'Vernieuwen...' : 'Vernieuwen'}
            </button>
          </div>
        </div>

        {isLoading ? (
          <div className="card">
            <div className="site-loading-block" role="status" aria-live="polite">
              <span className="site-loading-spinner" aria-hidden="true" />
              <div className="site-loading-copy">
                <h3>Beheercentrum laden</h3>
                <p>Gebruikers, support, surebets en systeemstatus worden opgehaald.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="admin-tab-content">
            {activeTab === 'overzicht' && (
              <div className="admin-dashboard">
                <div className="admin-section-heading">
                  <div>
                    <span className="admin-section-kicker">Vandaag</span>
                    <h2>Dit vraagt je aandacht</h2>
                    <p>De belangrijkste klant- en systeemsignalen, zonder technische ruis.</p>
                  </div>
                  <button className="card-btn card-btn-secondary" disabled={isRefreshing} onClick={() => { void refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi }); }} type="button">
                    {isRefreshing ? 'Bijwerken...' : 'Alles bijwerken'}
                  </button>
                </div>

                <div className="admin-attention-grid">
                  <button className={`admin-attention-card ${userSummary.attention > 0 ? 'has-warning' : 'is-clear'}`} onClick={() => { setUserTelegramFilter('attention'); setUserPage(1); setActiveTab('users'); }} type="button">
                    <span>Gebruikers</span>
                    <strong>{userSummary.attention}</strong>
                    <small>{userSummary.attention === 1 ? 'account vraagt aandacht' : 'accounts vragen aandacht'}</small>
                  </button>
                  <button className={`admin-attention-card ${supportSummary.newCount > 0 ? 'has-warning' : 'is-clear'}`} onClick={() => setActiveTab('support')} type="button">
                    <span>Support</span>
                    <strong>{supportSummary.newCount}</strong>
                    <small>{supportSummary.newCount === 1 ? 'nieuwe klantvraag' : 'nieuwe klantvragen'}</small>
                  </button>
                  <button className={`admin-attention-card ${appControl?.effectiveEnabled ? 'is-clear' : 'has-danger'}`} onClick={() => setActiveTab('system')} type="button">
                    <span>Systeem</span>
                    <strong>{appControl?.effectiveEnabled ? 'Online' : 'Controleer'}</strong>
                    <small>Prematch en live operatie</small>
                  </button>
                </div>

                <div className="admin-dashboard-grid">
                  <section className="card admin-dashboard-card">
                    <div className="admin-card-heading">
                      <div>
                        <span className="admin-section-kicker">Accounts</span>
                        <h3>Recente gebruikers</h3>
                      </div>
                      <button className="admin-text-button" onClick={() => setActiveTab('users')} type="button">Bekijk iedereen</button>
                    </div>
                    <div className="admin-compact-list">
                      {adminUsers.slice(0, 5).map((user) => (
                        <button className="admin-compact-user" key={user.id} onClick={() => { setSelectedUserId(user.id); setActiveTab('users'); }} type="button">
                          <span className="admin-user-avatar">{getInitials(user.name)}</span>
                          <span><strong>{user.name}</strong><small>{user.email}</small></span>
                          <span className={getBadgeClass(user.subscriptionStatus === 'active' ? 'success' : 'soft')}>{getPlanLabel(user.plan)}</span>
                        </button>
                      ))}
                      {adminUsers.length === 0 && <p className="admin-empty-copy">Nog geen gebruikers gevonden.</p>}
                    </div>
                  </section>

                  <section className="card admin-dashboard-card">
                    <div className="admin-card-heading">
                      <div>
                        <span className="admin-section-kicker">Live status</span>
                        <h3>Platform</h3>
                      </div>
                      <button className="admin-text-button" onClick={() => setActiveTab('system')} type="button">Open systeem</button>
                    </div>
                    <div className="admin-status-list">
                      <div className="admin-status-item"><span>Prematch scheduler</span>{renderBooleanBadge(schedulerStatus?.running ?? false, 'Actief', 'Stand-by')}</div>
                      <div className="admin-status-item"><span>Live worker</span>{renderBooleanBadge(liveWsStatus?.running ?? false, 'Actief', 'Stand-by')}</div>
                      <div className="admin-status-item"><span>Telegram prematch</span>{renderBooleanBadge(appControl?.prematchTelegramSendEnabled ?? false, 'Verstuurt', 'Dry')}</div>
                      <div className="admin-status-item"><span>Telegram live</span>{renderBooleanBadge(appControl?.liveTelegramSendEnabled ?? false, 'Verstuurt', 'Dry')}</div>
                      <div className="admin-status-item"><span>Minimale ROI</span><strong>{formatPercent(appControl?.minRoiPercent)}</strong></div>
                    </div>
                  </section>

                  <section className="card admin-dashboard-card admin-quick-card">
                    <span className="admin-section-kicker">Snel naar</span>
                    <h3>Veelgebruikte acties</h3>
                    <div className="admin-quick-actions-grid">
                      <button onClick={() => setActiveTab('users')} type="button"><strong>Gebruiker zoeken</strong><small>Abonnement of Telegram beheren</small></button>
                      <button onClick={() => setActiveTab('support')} type="button"><strong>Support openen</strong><small>{supportSummary.newCount} nieuw</small></button>
                      <button onClick={() => setActiveTab('surebet-history')} type="button"><strong>Surebets bekijken</strong><small>{storedSummary?.totalStored ?? 0} opgeslagen</small></button>
                      <Link to="/tracker"><strong>Tracker openen</strong><small>Bekijk klantweergave</small></Link>
                    </div>
                  </section>
                </div>
              </div>
            )}

            {activeTab === 'system' && (
              <div className="admin-system-section">
                <div className="admin-section-heading">
                  <div><span className="admin-section-kicker">Bediening</span><h2>Systeembeheer</h2><p>Beheer de live processen en verzending. Wijzig deze schakelaars alleen als je bewust een onderdeel wilt stoppen.</p></div>
                </div>
                <div className="admin-panel-grid">
                  <div className="card">
                    <div className="admin-control-form">
                      <label className="admin-toggle-row">
                        <span className="admin-field-label">
                          Volledig systeem
                          <span>Zet de scheduler en live worker gezamenlijk aan of uit.</span>
                        </span>
                        <input
                          checked={controlDraft.enabled}
                          onChange={(event) => setControlDraft((current) => ({ ...current, enabled: event.target.checked }))}
                          type="checkbox"
                        />
                      </label>
                      <label className="admin-toggle-row">
                        <span className="admin-field-label">
                          Prematch-proces
                          <span>Start of stop alleen de prematch scheduler zonder de live worker te raken.</span>
                        </span>
                        <input
                          checked={controlDraft.prematchEnabled}
                          onChange={(event) => setControlDraft((current) => ({ ...current, prematchEnabled: event.target.checked }))}
                          type="checkbox"
                        />
                      </label>
                      <label className="admin-toggle-row">
                        <span className="admin-field-label">
                          Live-proces
                          <span>Start of stop alleen de live websocket worker zonder de prematch scheduler te raken.</span>
                        </span>
                        <input
                          checked={controlDraft.liveEnabled}
                          onChange={(event) => setControlDraft((current) => ({ ...current, liveEnabled: event.target.checked }))}
                          type="checkbox"
                        />
                      </label>
                      <label className="admin-toggle-row">
                        <span className="admin-field-label">
                          Prematchmeldingen versturen
                          <span>Schakel echte Telegramverzending voor prematch in zonder nieuwe deploy.</span>
                        </span>
                        <input
                          checked={controlDraft.prematchTelegramSendEnabled}
                          onChange={(event) => setControlDraft((current) => ({ ...current, prematchTelegramSendEnabled: event.target.checked }))}
                          type="checkbox"
                        />
                      </label>
                      <label className="admin-toggle-row">
                        <span className="admin-field-label">
                          Livemeldingen versturen
                          <span>Schakel echte Telegramverzending voor live in zonder nieuwe deploy.</span>
                        </span>
                        <input
                          checked={controlDraft.liveTelegramSendEnabled}
                          onChange={(event) => setControlDraft((current) => ({ ...current, liveTelegramSendEnabled: event.target.checked }))}
                          type="checkbox"
                        />
                      </label>
                      <div className="admin-filter-group">
                        <label className="admin-field-label" htmlFor="admin-min-roi">
                          Minimale ROI (%)
                          <span>Wordt gebruikt door scheduler, live websocket en tracker history default filtering.</span>
                        </label>
                        <input
                          className="admin-search-input"
                          id="admin-min-roi"
                          inputMode="decimal"
                          onChange={(event) => setControlDraft((current) => ({ ...current, minRoiPercent: event.target.value }))}
                          type="number"
                          min="0"
                          step="0.1"
                          value={controlDraft.minRoiPercent}
                        />
                      </div>
                      <div className="admin-inline-actions">
                        <button className="card-btn" disabled={isSaving} aria-busy={isSaving} onClick={() => { void handleSaveControls(); }} type="button">
                          {isSaving ? 'Opslaan...' : 'Instellingen opslaan'}
                        </button>
                        <button className="card-btn card-btn-secondary" disabled={isRefreshing} aria-busy={isRefreshing} onClick={() => { void refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi }); }} type="button">
                          Huidige status herladen
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="card">
                    <h3>Actieve backendstatus</h3>
                    <div className="admin-status-list">
                      <div className="admin-status-item"><span>Omgeving</span><strong>{appControl?.environment || 'onbekend'}</strong></div>
                      <div className="admin-status-item"><span>Scheduler geconfigureerd</span>{renderBooleanBadge(appControl?.schedulerConfigured ?? false, 'Ja', 'Nee')}</div>
                      <div className="admin-status-item"><span>Live WS geconfigureerd</span>{renderBooleanBadge(appControl?.liveWsConfigured ?? false, 'Ja', 'Nee')}</div>
                      <div className="admin-status-item"><span>Prematch-proces</span>{renderBooleanBadge(appControl?.prematchEnabled ?? false, 'Aan', 'Uit')}</div>
                      <div className="admin-status-item"><span>Live-proces</span>{renderBooleanBadge(appControl?.liveEnabled ?? false, 'Aan', 'Uit')}</div>
                      <div className="admin-status-item"><span>Voorbeeldinzet</span><strong>{formatMoney(appControl?.totalStake)}</strong></div>
                      <div className="admin-status-item"><span>Max. meldingen per run</span><strong>{appControl?.maxSendsPerTick ?? '-'}</strong></div>
                    </div>
                    {appControl?.providerConfigError && (
                      <div className="admin-alert admin-alert-warning">
                        Provider config fout: {appControl.providerConfigError}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'support' && (
              <div>
                <div className="admin-section-heading"><div><span className="admin-section-kicker">Inbox</span><h2>Klantsupport</h2><p>Behandel vragen vanaf de contactpagina en houd de voortgang per aanvraag bij.</p></div></div>
                <div className="admin-user-summary-grid admin-support-summary-grid">
                  <button className={supportStatusFilter === 'all' ? 'is-active' : ''} onClick={() => { setSupportStatusFilter('all'); setSupportPage(1); }} type="button"><strong>{supportSummary.total}</strong><span>Alle vragen</span></button>
                  <button className={supportStatusFilter === 'new' ? 'is-active has-warning' : 'has-warning'} onClick={() => { setSupportStatusFilter('new'); setSupportPage(1); }} type="button"><strong>{supportSummary.newCount}</strong><span>Nieuw</span></button>
                  <button className={supportStatusFilter === 'in_progress' ? 'is-active' : ''} onClick={() => { setSupportStatusFilter('in_progress'); setSupportPage(1); }} type="button"><strong>{supportSummary.inProgressCount}</strong><span>In behandeling</span></button>
                  <button className={supportStatusFilter === 'closed' ? 'is-active' : ''} onClick={() => { setSupportStatusFilter('closed'); setSupportPage(1); }} type="button"><strong>{supportSummary.closedCount}</strong><span>Afgerond</span></button>
                </div>

                <div className="card admin-support-filter-card">
                  <div className="admin-filter-bar">
                    <div className="admin-filter-group">
                      <label className="admin-field-label" htmlFor="support-status-filter">Filter op status</label>
                      <select
                        className="admin-search-input"
                        id="support-status-filter"
                        onChange={(event) => {
                          setSupportStatusFilter(event.target.value as SupportRequestStatusFilter);
                          setSupportPage(1);
                        }}
                        value={supportStatusFilter}
                      >
                        <option value="all">Alle statussen</option>
                        {SUPPORT_REQUEST_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="admin-table-wrap">
                  <div className="admin-table-toolbar">
                    <div className="admin-helper-text">Pagina {supportPage} van {supportPageCount} | {filteredSupportRequests.length} van {supportSummary.total} supportvragen</div>
                  </div>
                  <table className="admin-table admin-support-table">
                    <thead>
                      <tr>
                        <th>Ontvangen</th>
                        <th>Afzender</th>
                        <th>Onderwerp</th>
                        <th>Bericht</th>
                        <th>Mailstatus</th>
                        <th>Afhandeling</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredSupportRequests.length === 0 ? (
                        <tr>
                          <td colSpan={6}>Nog geen supportvragen voor deze filter.</td>
                        </tr>
                      ) : visibleSupportRequests.map((requestItem) => {
                        const draftStatus = supportRequestDrafts[requestItem.id] ?? requestItem.status;
                        const isDirty = draftStatus !== requestItem.status;
                        return (
                          <tr key={requestItem.id}>
                            <td>
                              <strong>#{requestItem.id}</strong>
                              <div className="admin-table-subtext">{formatDateTime(requestItem.createdAt)}</div>
                            </td>
                            <td>
                              <strong>{requestItem.name}</strong>
                              <a className="admin-table-subtext admin-table-link" href={`mailto:${requestItem.email}`}>{requestItem.email}</a>
                              <div className="admin-table-subtext">Pakket: {requestItem.planLabel}</div>
                            </td>
                            <td>
                              <span className={getBadgeClass(getSupportRequestStatusVariant(requestItem.status))}>{getSupportRequestStatusLabel(requestItem.status)}</span>
                              <div className="admin-table-subtext">{requestItem.topicLabel}</div>
                            </td>
                            <td>
                              <div className="admin-support-message">{requestItem.message}</div>
                            </td>
                            <td>
                              <span className={getBadgeClass(getSupportMailStatusVariant(requestItem.mailDeliveryStatus))}>{getSupportMailStatusLabel(requestItem.mailDeliveryStatus)}</span>
                              <div className="admin-table-subtext">Verzonden: {formatDateTime(requestItem.mailSentAt)}</div>
                              {requestItem.mailDeliveryError && <div className="admin-table-subtext admin-danger-text">{requestItem.mailDeliveryError}</div>}
                            </td>
                            <td>
                              <div className="admin-user-actions">
                                <a className="card-btn card-btn-sm card-link card-btn-secondary" href={`mailto:${requestItem.email}?subject=${encodeURIComponent(`Re: ${requestItem.topicLabel}`)}`}>Beantwoorden</a>
                                <select
                                  className="admin-table-select"
                                  value={draftStatus}
                                  onChange={(event) => setSupportRequestDrafts((current) => ({
                                    ...current,
                                    [requestItem.id]: event.target.value,
                                  }))}
                                >
                                  {SUPPORT_REQUEST_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                                </select>
                                <button
                                  className="card-btn card-btn-sm"
                                  disabled={!isDirty || savingSupportRequestId === requestItem.id}
                                  aria-busy={savingSupportRequestId === requestItem.id}
                                  onClick={() => { void handleSaveSupportRequest(requestItem.id); }}
                                  type="button"
                                >
                                  {savingSupportRequestId === requestItem.id ? 'Opslaan...' : 'Opslaan'}
                                </button>
                              </div>
                              <div className="admin-table-subtext">Afgehandeld: {formatDateTime(requestItem.handledAt)}</div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="admin-pagination">
                    <button className="card-btn card-btn-sm" disabled={supportPage <= 1} onClick={() => setSupportPage((page) => Math.max(1, page - 1))} type="button">Vorige</button>
                    <span>Pagina {supportPage} / {supportPageCount}</span>
                    <button className="card-btn card-btn-sm" disabled={supportPage >= supportPageCount} onClick={() => setSupportPage((page) => Math.min(supportPageCount, page + 1))} type="button">Volgende</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'users' && (
              <div className="admin-users-page">
                <div className="admin-section-heading">
                  <div>
                    <span className="admin-section-kicker">Klantbeheer</span>
                    <h2>Gebruikers</h2>
                    <p>Vind een account, controleer toegang en beheer abonnement en Telegram vanuit één detailpaneel.</p>
                  </div>
                </div>

                <div className="admin-user-summary-grid">
                  <button className={userPlanFilter === 'all' ? 'is-active' : ''} onClick={() => { setUserPlanFilter('all'); setUserPage(1); }} type="button"><strong>{userSummary.total}</strong><span>Totaal</span></button>
                  <button className={userPlanFilter === 'active' ? 'is-active' : ''} onClick={() => { setUserPlanFilter('active'); setUserPage(1); }} type="button"><strong>{userSummary.active}</strong><span>Actief</span></button>
                  <button className={userPlanFilter === 'stripe' ? 'is-active' : ''} onClick={() => { setUserPlanFilter('stripe'); setUserPage(1); }} type="button"><strong>{userSummary.stripe}</strong><span>Via Stripe</span></button>
                  <button className={userPlanFilter === 'manual' ? 'is-active' : ''} onClick={() => { setUserPlanFilter('manual'); setUserPage(1); }} type="button"><strong>{userSummary.manual}</strong><span>Handmatig</span></button>
                  <button className={userTelegramFilter === 'attention' ? 'has-warning is-active' : userSummary.attention > 0 ? 'has-warning' : ''} onClick={() => { setUserTelegramFilter('attention'); setUserPage(1); }} type="button"><strong>{userSummary.attention}</strong><span>Aandacht nodig</span></button>
                </div>

                <div className="card admin-user-filter-card">
                  <div className="admin-user-search-field">
                    <label htmlFor="admin-user-search">Zoeken</label>
                    <input id="admin-user-search" className="admin-search-input" onChange={(event) => { setUserSearch(event.target.value); setUserPage(1); }} placeholder="Naam, e-mail, Telegram, Stripe-ID of gebruikers-ID" type="search" value={userSearch} />
                  </div>
                  <div className="admin-filter-group">
                    <label htmlFor="admin-user-plan">Abonnement</label>
                    <select id="admin-user-plan" className="admin-search-input" onChange={(event) => { setUserPlanFilter(event.target.value as AdminUserPlanFilter); setUserPage(1); }} value={userPlanFilter}>
                      <option value="all">Alle abonnementen</option>
                      <option value="active">Alleen actieve toegang</option>
                      <option value="stripe">Beheerd via Stripe</option>
                      <option value="manual">Handmatig toegekend</option>
                      <option value="none">Geen abonnement</option>
                      <option value="pro">Pro</option>
                      <option value="pre-match">Pre-match</option>
                      <option value="live">Live</option>
                    </select>
                  </div>
                  <div className="admin-filter-group">
                    <label htmlFor="admin-user-telegram">Telegram</label>
                    <select id="admin-user-telegram" className="admin-search-input" onChange={(event) => { setUserTelegramFilter(event.target.value as AdminUserTelegramFilter); setUserPage(1); }} value={userTelegramFilter}>
                      <option value="all">Alle statussen</option>
                      <option value="linked">Gekoppeld</option>
                      <option value="unlinked">Niet gekoppeld</option>
                      <option value="attention">Actie nodig</option>
                    </select>
                  </div>
                  <div className="admin-filter-group">
                    <label htmlFor="admin-user-sort">Sorteren</label>
                    <select id="admin-user-sort" className="admin-search-input" onChange={(event) => setUserSort(event.target.value as AdminUserSort)} value={userSort}>
                      <option value="newest">Nieuwste eerst</option>
                      <option value="last-login">Laatst ingelogd</option>
                      <option value="name">Naam A-Z</option>
                      <option value="subscription-end">Einddatum abonnement</option>
                    </select>
                  </div>
                  <button className="admin-clear-filters" disabled={!userSearch && userPlanFilter === 'all' && userTelegramFilter === 'all' && userSort === 'newest'} onClick={() => { setUserSearch(''); setUserPlanFilter('all'); setUserTelegramFilter('all'); setUserSort('newest'); setUserPage(1); }} type="button">Filters wissen</button>
                </div>

                <div className={`admin-users-workspace ${selectedUser ? 'has-selection' : ''}`}>
                  <section className="admin-user-list-panel" aria-label="Gebruikerslijst">
                    <div className="admin-list-heading">
                      <span><strong>{filteredAdminUsers.length}</strong> van {userSummary.total} gebruikers</span>
                      <small>Pagina {userPage} van {userPageCount}</small>
                    </div>
                    <div className="admin-user-list">
                      {visibleAdminUsers.map((user) => {
                        const hasActiveAccess = (user.subscriptionStatus === 'active' || user.subscriptionStatus === 'trialing') && user.plan !== 'none';
                        const needsAttention = user.subscriptionStatus === 'past_due' || user.telegramAccessStatus === 'error' || (hasActiveAccess && !user.telegramLinked);
                        return (
                          <button className={`admin-user-row ${selectedUserId === user.id ? 'is-selected' : ''}`} key={user.id} onClick={() => setSelectedUserId(user.id)} type="button">
                            <span className="admin-user-avatar">{getInitials(user.name)}</span>
                            <span className="admin-user-row-main">
                              <span className="admin-user-row-title"><strong>{user.name}</strong>{user.role === 'admin' && <span className={getBadgeClass('soft')}>Admin</span>}</span>
                              <small>{user.email}</small>
                              <span className="admin-user-row-meta">Laatste login: {formatDateTime(user.lastLoginAt)}</span>
                            </span>
                            <span className="admin-user-row-status">
                              <span className={getBadgeClass(hasActiveAccess ? 'success' : user.subscriptionStatus === 'past_due' ? 'danger' : 'soft')}>{getPlanLabel(user.plan)}</span>
                              <small>{user.billingManagedByStripe ? 'Stripe' : hasActiveAccess ? 'Handmatig' : 'Geen betaling'}</small>
                            </span>
                            <span className={`admin-user-signal ${needsAttention ? 'has-warning' : ''}`} aria-label={needsAttention ? 'Actie nodig' : 'Geen aandachtspunten'} />
                          </button>
                        );
                      })}
                      {visibleAdminUsers.length === 0 && (
                        <div className="admin-empty-state"><strong>Geen gebruikers gevonden</strong><span>Pas je zoekopdracht of filters aan.</span></div>
                      )}
                    </div>
                    <div className="admin-pagination">
                      <button className="card-btn card-btn-sm" disabled={userPage <= 1} onClick={() => setUserPage((page) => Math.max(1, page - 1))} type="button">Vorige</button>
                      <span>{userPage} / {userPageCount}</span>
                      <button className="card-btn card-btn-sm" disabled={userPage >= userPageCount} onClick={() => setUserPage((page) => Math.min(userPageCount, page + 1))} type="button">Volgende</button>
                    </div>
                  </section>

                  <aside className="admin-user-detail" aria-label="Gebruikersdetails">
                    {selectedUser ? (() => {
                      const draft = adminUserDrafts[selectedUser.id] ?? toAdminUserDraft(selectedUser);
                      const original = toAdminUserDraft(selectedUser);
                      const endDateChanged = draft.subscriptionEndsAt !== original.subscriptionEndsAt;
                      const isDirty = draft.role !== original.role || draft.plan !== original.plan || draft.subscriptionStatus !== original.subscriptionStatus || endDateChanged;
                      const isSendingInvites = sendingUserInvitesId === selectedUser.id;
                      const isDeletingUser = deletingUserId === selectedUser.id;
                      const isConfirmingDelete = confirmDeleteUserId === selectedUser.id;
                      const hasActiveAccess = (selectedUser.subscriptionStatus === 'active' || selectedUser.subscriptionStatus === 'trialing') && selectedUser.plan !== 'none';
                      return (
                        <>
                          <div className="admin-user-detail-header">
                            <div className="admin-user-identity">
                              <span className="admin-user-avatar admin-user-avatar-lg">{getInitials(selectedUser.name)}</span>
                              <div><span className="admin-section-kicker">Gebruiker #{selectedUser.id}</span><h3>{selectedUser.name}</h3><a href={`mailto:${selectedUser.email}`}>{selectedUser.email}</a></div>
                            </div>
                            <button aria-label="Sluit gebruikersdetails" className="admin-detail-close" onClick={() => setSelectedUserId(null)} type="button">×</button>
                          </div>

                          {(selectedUser.subscriptionStatus === 'past_due' || selectedUser.telegramAccessStatus === 'error' || (hasActiveAccess && !selectedUser.telegramLinked)) && (
                            <div className="admin-user-warning">
                              <strong>Controle nodig</strong>
                              <span>{selectedUser.subscriptionStatus === 'past_due' ? 'De laatste abonnementsbetaling is mislukt.' : selectedUser.telegramAccessStatus === 'error' ? selectedUser.telegramAccessError || 'De Telegramsynchronisatie heeft een fout.' : 'Dit actieve account heeft Telegram nog niet gekoppeld.'}</span>
                            </div>
                          )}

                          <div className="admin-detail-metrics">
                            <div><span>Trackeritems</span><strong>{selectedUser.trackedSurebetCount ?? 0}</strong><small>{formatDateTime(selectedUser.lastTrackerActivityAt)}</small></div>
                            <div><span>Stripebetalingen</span><strong>{selectedUser.stripePaymentCount ?? 0}</strong><small>{formatMoney(selectedUser.stripePaidTotal ?? 0)}</small></div>
                            <div><span>Vrienden aangebracht</span><strong>{selectedUser.qualifiedReferrals ?? 0}</strong><small>{selectedUser.availableReferralRewards ?? 0} korting(en) klaar</small></div>
                            <div><span>Account sinds</span><strong>{formatDateTime(selectedUser.createdAt)}</strong><small>Laatst ingelogd {formatDateTime(selectedUser.lastLoginAt)}</small></div>
                          </div>

                          <section className="admin-detail-section">
                            <div className="admin-card-heading"><div><span className="admin-section-kicker">Toegang</span><h4>Rol en abonnement</h4></div><span className={getBadgeClass(hasActiveAccess ? 'success' : 'soft')}>{getSubscriptionStatusLabel(selectedUser.subscriptionStatus)}</span></div>
                            <div className="admin-detail-form-grid">
                              <label><span>Rol</span><select className="admin-table-select" value={draft.role} onChange={(event) => setAdminUserDrafts((current) => ({ ...current, [selectedUser.id]: { ...draft, role: event.target.value as AdminUserDraft['role'] } }))}>{ADMIN_USER_ROLE_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                              <label><span>Pakket</span><select className="admin-table-select" value={draft.plan} onChange={(event) => { const plan = event.target.value as AdminUserDraft['plan']; setAdminUserDrafts((current) => ({ ...current, [selectedUser.id]: { ...draft, plan, subscriptionStatus: plan === 'none' ? 'none' : 'active' } })); }}>{ADMIN_USER_PLAN_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                              <label><span>Status</span><select className="admin-table-select" value={draft.subscriptionStatus} onChange={(event) => setAdminUserDrafts((current) => ({ ...current, [selectedUser.id]: { ...draft, subscriptionStatus: event.target.value as AdminUserDraft['subscriptionStatus'] } }))}>{ADMIN_USER_STATUS_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                              <label><span>Einddatum</span><input className="admin-table-select" disabled={draft.plan === 'none' || draft.subscriptionStatus !== 'active'} min={getTodayDateInputValue()} onChange={(event) => setAdminUserDrafts((current) => ({ ...current, [selectedUser.id]: { ...draft, subscriptionEndsAt: event.target.value } }))} type="date" value={draft.subscriptionEndsAt} /><small>Leeg laten geeft toegang zonder automatische einddatum.</small></label>
                            </div>
                            <div className="admin-subscription-summary">
                              <div><span>Beheer</span><strong>{selectedUser.billingManagedByStripe ? 'Stripe' : hasActiveAccess ? 'Handmatig' : 'Geen betaalprovider'}</strong></div>
                              <div><span>Looptijd</span><strong>{getBillingCycleLabel(selectedUser.billingCycle)}</strong></div>
                              <div><span>Einddatum</span><strong>{formatDateTime(selectedUser.subscriptionEndsAt)}</strong></div>
                            </div>
                            <button className="card-btn" disabled={!isDirty || savingUserId === selectedUser.id || isDeletingUser || isSendingInvites} onClick={() => { void handleSaveUser(selectedUser.id); }} type="button">{savingUserId === selectedUser.id ? 'Opslaan...' : isDirty ? 'Wijzigingen opslaan' : 'Geen wijzigingen'}</button>
                            {selectedUser.billingManagedByStripe && (endDateChanged || draft.plan !== original.plan || draft.subscriptionStatus !== original.subscriptionStatus) && <p className="admin-detail-note">Let op: deze wijziging stopt het huidige Stripe-abonnement. Het account krijgt daarna handmatig beheerde toegang tot de gekozen einddatum.</p>}
                          </section>

                          <section className="admin-detail-section">
                            <div className="admin-card-heading"><div><span className="admin-section-kicker">Telegram</span><h4>{selectedUser.telegramLinked ? 'Account gekoppeld' : 'Niet gekoppeld'}</h4></div><span className={getBadgeClass(selectedUser.telegramAccessStatus === 'joined' || selectedUser.telegramAccessStatus === 'invited' ? 'success' : selectedUser.telegramAccessStatus === 'error' ? 'danger' : 'soft')}>{getTelegramStatusLabel(selectedUser.telegramAccessStatus)}</span></div>
                            <div className="admin-detail-info-list">
                              <div><span>Telegramaccount</span><strong>{selectedUser.telegramLinked ? selectedUser.telegramUsername ? `@${selectedUser.telegramUsername}` : selectedUser.telegramChatId : 'Niet gekoppeld'}</strong></div>
                              <div><span>Gekoppeld op</span><strong>{formatDateTime(selectedUser.telegramLinkedAt)}</strong></div>
                              <div><span>Laatste synchronisatie</span><strong>{formatDateTime(selectedUser.telegramAccessSyncedAt)}</strong></div>
                            </div>
                            <button className="card-btn admin-telegram-invite-action" disabled={!selectedUser.telegramLinked || !hasActiveAccess || isSendingInvites || isDeletingUser || savingUserId === selectedUser.id} onClick={() => { void handleSendUserTelegramInvites(selectedUser.id); }} type="button">{isSendingInvites ? 'Versturen...' : 'Kanaallinks via bot sturen'}</button>
                          </section>

                          <section className="admin-detail-section">
                            <span className="admin-section-kicker">Snelle acties</span>
                            <div className="admin-detail-actions">
                              <a className="card-btn card-link" href={`mailto:${selectedUser.email}`}>E-mail sturen</a>
                              <button className="card-btn card-btn-secondary" onClick={() => { void handleCopy(selectedUser.email, 'E-mailadres'); }} type="button">E-mail kopiëren</button>
                              {selectedUser.telegramChatId && <button className="card-btn card-btn-secondary" onClick={() => { void handleCopy(selectedUser.telegramChatId || '', 'Telegram-ID'); }} type="button">Telegram-ID kopiëren</button>}
                              {selectedUser.stripeCustomerId && <a className="card-btn card-link card-btn-secondary" href={`https://dashboard.stripe.com/customers/${selectedUser.stripeCustomerId}`} rel="noreferrer" target="_blank">Open in Stripe</a>}
                            </div>
                          </section>

                          <section className="admin-detail-section admin-danger-zone">
                            <div><strong>Account verwijderen</strong><p>Dit verwijdert de gebruiker en gekoppelde lokale gegevens definitief.</p></div>
                            {isConfirmingDelete ? (
                              <div className="admin-inline-actions"><button className="card-btn admin-danger-action" disabled={isDeletingUser} onClick={() => { void handleDeleteUser(selectedUser.id); }} type="button">{isDeletingUser ? 'Verwijderen...' : 'Definitief verwijderen'}</button><button className="card-btn card-btn-secondary" disabled={isDeletingUser} onClick={() => setConfirmDeleteUserId(null)} type="button">Annuleren</button></div>
                            ) : <button className="admin-text-button admin-danger-text" onClick={() => setConfirmDeleteUserId(selectedUser.id)} type="button">Account verwijderen</button>}
                          </section>
                        </>
                      );
                    })() : (
                      <div className="admin-detail-empty"><span className="admin-user-avatar admin-user-avatar-lg">PO</span><strong>Selecteer een gebruiker</strong><p>Bekijk abonnement, betalingen, trackeractiviteit en Telegramstatus zonder de lijst te verlaten.</p></div>
                    )}
                  </aside>
                </div>
              </div>
            )}

            {activeTab === 'system' && (
              <div className="admin-system-section">
                <div className="admin-section-heading"><div><span className="admin-section-kicker">Kanaalcontrole</span><h2>Telegram testen</h2><p>Stuur één gecontroleerde testmelding naar het prematch- of livekanaal.</p></div></div>
                <div className="admin-panel-grid">
                  <div className="card">
                    <h3>Prematch kanaal</h3>
                    <p>Stuurt een willekeurige prematch surebet met de huidige runtime ROI-grens.</p>
                    <button
                      className="card-btn"
                      disabled={sendingChannel !== null}
                      onClick={() => { void handleTelegramTest('prematch'); }}
                      type="button"
                    >
                      {sendingChannel === 'prematch' ? 'Versturen...' : 'Stuur prematch test'}
                    </button>
                  </div>
                  <div className="card">
                    <h3>Live kanaal</h3>
                    <p>Stuurt een willekeurige live surebet met dezelfde runtime-control instellingen als de worker.</p>
                    <button
                      className="card-btn"
                      disabled={sendingChannel !== null}
                      onClick={() => { void handleTelegramTest('live'); }}
                      type="button"
                    >
                      {sendingChannel === 'live' ? 'Versturen...' : 'Stuur live test'}
                    </button>
                  </div>
                  <div className="card">
                    <h3>Huidige verzendmodus</h3>
                    <div className="admin-status-list">
                      <div className="admin-status-item"><span>Prematch Telegram</span>{renderBooleanBadge(appControl?.prematchTelegramSendEnabled ?? false, 'Live', 'Dry')}</div>
                      <div className="admin-status-item"><span>Live Telegram</span>{renderBooleanBadge(appControl?.liveTelegramSendEnabled ?? false, 'Live', 'Dry')}</div>
                      <div className="admin-status-item"><span>Schedulermodus</span><strong>{schedulerStatus?.mode || 'onbekend'}</strong></div>
                      <div className="admin-status-item"><span>Workermodus</span><strong>{liveWsStatus?.mode || 'onbekend'}</strong></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'surebet-history' && (
              <div>
                <div className="admin-section-heading"><div><span className="admin-section-kicker">Meldingen</span><h2>Surebetbeheer</h2><p>Controleer gevonden kansen en bekijk welke meldingen daadwerkelijk zijn verzonden.</p></div></div>

                <div className="card" style={{ marginBottom: '16px' }}>
                  <div className="admin-filter-bar">
                    <div className="admin-filter-group">
                      <label className="admin-field-label" htmlFor="stored-search">Zoek gevonden surebets</label>
                      <input
                        className="admin-search-input"
                        id="stored-search"
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Event, market of candidate key"
                        type="text"
                        value={search}
                      />
                    </div>
                    <div className="admin-filter-group">
                      <label className="admin-field-label" htmlFor="stored-min-roi">Min ROI filter</label>
                      <input
                        className="admin-search-input"
                        id="stored-min-roi"
                        inputMode="decimal"
                        min="0"
                        onChange={(event) => setStoredMinRoi(event.target.value)}
                        placeholder="Bijv. 4.0"
                        step="0.1"
                        type="number"
                        value={storedMinRoi}
                      />
                    </div>
                    <div className="admin-filter-group">
                      <label className="admin-field-label" htmlFor="stored-type-filter">Live / Pre-match</label>
                      <select
                        className="admin-search-input"
                        id="stored-type-filter"
                        onChange={(event) => {
                          setStoredTypeFilter(event.target.value as StoredTypeFilter);
                          setStoredPage(1);
                        }}
                        value={storedTypeFilter}
                      >
                        <option value="all">Alles</option>
                        <option value="live">Live</option>
                        <option value="prematch">Pre-match</option>
                      </select>
                    </div>
                    <div className="admin-filter-group">
                      <label className="admin-field-label" htmlFor="stored-status-filter">Status</label>
                      <select
                        className="admin-search-input"
                        id="stored-status-filter"
                        onChange={(event) => {
                          setStoredStatusFilter(event.target.value);
                          setStoredPage(1);
                        }}
                        value={storedStatusFilter}
                      >
                        <option value="all">Alle statussen</option>
                        {storedStatuses.map((status) => <option key={status} value={status}>{status}</option>)}
                      </select>
                    </div>
                    <div className="admin-filter-group">
                      <label className="admin-field-label" htmlFor="stored-sort">Sorteren</label>
                      <select
                        className="admin-search-input"
                        id="stored-sort"
                        onChange={(event) => {
                          setStoredSort(event.target.value as StoredSortOption);
                          setStoredPage(1);
                        }}
                        value={storedSort}
                      >
                        <option value="sent-desc">Laatst verstuurde surebets nieuw-oud</option>
                        <option value="sent-asc">Laatst verstuurde surebets oud-nieuw</option>
                        <option value="roi-desc">ROI hoog-laag</option>
                        <option value="roi-asc">ROI laag-hoog</option>
                        <option value="type-asc">Type A-Z</option>
                        <option value="status-asc">Status A-Z</option>
                      </select>
                    </div>
                    <div className="admin-filter-actions">
                      <button className="card-btn" onClick={() => { void refreshDashboard({ silent: true, searchTerm: search, minRoiTerm: storedMinRoi }); }} type="button">Filter toepassen</button>
                    </div>
                  </div>
                  <div className="admin-helper-text">Opgeslagen: {storedSummary?.totalStored ?? 0} | Gevonden: {storedSummary?.found ?? 0} | Bijgewerkt: {storedSummary?.updated ?? 0} | Verzonden: {storedSummary?.sent ?? 0}</div>
                </div>

                <div className="admin-table-wrap">
                  <div className="admin-table-toolbar">
                    <div className="admin-helper-text">Pagina {storedPage} van {storedPageCount} | {filteredStoredCandidates.length} van {storedCandidates.length} gevonden surebets</div>
                    <button
                      className="card-btn card-btn-sm admin-danger-action"
                      disabled={selectedStoredIds.length === 0 || isDeletingStored}
                      onClick={() => { void handleDeleteStored(); }}
                      type="button"
                    >
                      {isDeletingStored ? 'Verwijderen...' : `Verwijder geselecteerd (${selectedStoredIds.length})`}
                    </button>
                  </div>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            aria-label="Selecteer alle stored surebets op deze pagina"
                            checked={allVisibleStoredSelected}
                            onChange={(event) => {
                              setSelectedStoredIds((current) => event.target.checked
                                ? Array.from(new Set([...current, ...visibleStoredIds]))
                                : current.filter((id) => !visibleStoredIds.includes(id)));
                            }}
                            type="checkbox"
                          />
                        </th>
                        <th>Status</th>
                        <th>Laatst gezien</th>
                        <th>Event</th>
                        <th>Type</th>
                        <th>Markt</th>
                        <th>Bookmakers</th>
                        <th>ROI</th>
                        <th>Min winst</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStoredCandidates.length === 0 ? (
                        <tr>
                          <td colSpan={9}>Geen gevonden surebets voor deze filters.</td>
                        </tr>
                      ) : visibleStoredCandidates.map((candidate: StoredSurebetCandidateApiItem) => (
                        <tr key={candidate.candidateKey}>
                          <td>
                            <input
                              aria-label={`Selecteer ${candidate.eventName}`}
                              checked={selectedStoredIds.includes(candidate.id)}
                              onChange={(event) => {
                                setSelectedStoredIds((current) => event.target.checked
                                  ? Array.from(new Set([...current, candidate.id]))
                                  : current.filter((id) => id !== candidate.id));
                              }}
                              type="checkbox"
                            />
                          </td>
                          <td><span className={getBadgeClass(candidate.status === 'sent' ? 'success' : candidate.status === 'ignored' ? 'warning' : 'soft')}>{candidate.status}</span></td>
                          <td>{formatDateTime(candidate.lastSeenAt)}</td>
                          <td>
                            <strong>{candidate.eventName}</strong>
                            <div className="admin-table-subtext">{candidate.competition || 'Onbekende competitie'}</div>
                          </td>
                          <td>{candidate.candidateType}</td>
                          <td>{candidate.marketName}</td>
                          <td>{candidate.bookmakerA.name} / {candidate.bookmakerB.name}</td>
                          <td>{formatPercent(candidate.roiPercent)}</td>
                          <td>{formatMoney(candidate.stakeExample.minProfit)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="admin-pagination">
                    <button className="card-btn card-btn-sm" disabled={storedPage <= 1} onClick={() => setStoredPage((page) => Math.max(1, page - 1))} type="button">Vorige</button>
                    <span>Pagina {storedPage} / {storedPageCount}</span>
                    <button className="card-btn card-btn-sm" disabled={storedPage >= storedPageCount} onClick={() => setStoredPage((page) => Math.min(storedPageCount, page + 1))} type="button">Volgende</button>
                  </div>
                </div>

                <h3 style={{ marginTop: '24px' }}>Verzonden meldingen</h3>
                <div className="admin-table-wrap">
                  <div className="admin-table-toolbar">
                    <div className="admin-helper-text">Pagina {trackerHistoryPage} van {trackerHistoryPageCount} | {trackerHistory.length} verzonden surebets</div>
                    <button
                      className="card-btn card-btn-sm admin-danger-action"
                      disabled={selectedTrackerHistoryIds.length === 0 || isDeletingTrackerHistory}
                      onClick={() => { void handleDeleteTrackerHistory(); }}
                      type="button"
                    >
                      {isDeletingTrackerHistory ? 'Verwijderen...' : `Verwijder geselecteerd (${selectedTrackerHistoryIds.length})`}
                    </button>
                  </div>
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            aria-label="Selecteer alle tracker history items op deze pagina"
                            checked={allVisibleTrackerHistorySelected}
                            onChange={(event) => {
                              setSelectedTrackerHistoryIds((current) => event.target.checked
                                ? Array.from(new Set([...current, ...visibleTrackerHistoryIds]))
                                : current.filter((id) => !visibleTrackerHistoryIds.includes(id)));
                            }}
                            type="checkbox"
                          />
                        </th>
                        <th>Verzonden op</th>
                        <th>Event</th>
                        <th>Markt</th>
                        <th>Type</th>
                        <th>Bookmakers</th>
                        <th>ROI</th>
                        <th>Totale stake</th>
                        <th>Min winst</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trackerHistory.length === 0 ? (
                        <tr>
                          <td colSpan={9}>Nog geen verzonden surebets in tracker history.</td>
                        </tr>
                      ) : visibleTrackerHistory.map((item) => {
                        const numericId = getTrackerHistoryNumericId(item.id);
                        return (
                        <tr key={item.id}>
                          <td>
                            <input
                              aria-label={`Selecteer ${item.eventName}`}
                              checked={numericId !== null && selectedTrackerHistoryIds.includes(numericId)}
                              disabled={numericId === null}
                              onChange={(event) => {
                                if (numericId === null) return;
                                setSelectedTrackerHistoryIds((current) => event.target.checked
                                  ? Array.from(new Set([...current, numericId]))
                                  : current.filter((id) => id !== numericId));
                              }}
                              type="checkbox"
                            />
                          </td>
                          <td>{formatDateTime(item.createdAt)}</td>
                          <td>{item.eventName}</td>
                          <td>{item.market}</td>
                          <td>{item.type}</td>
                          <td>{item.bookmakerA} / {item.bookmakerB}</td>
                          <td>{formatPercent(item.roiPercent)}</td>
                          <td>{formatMoney(item.totalStake)}</td>
                          <td>{formatMoney(item.minimumProfit)}</td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  <div className="admin-pagination">
                    <button className="card-btn card-btn-sm" disabled={trackerHistoryPage <= 1} onClick={() => setTrackerHistoryPage((page) => Math.max(1, page - 1))} type="button">Vorige</button>
                    <span>Pagina {trackerHistoryPage} / {trackerHistoryPageCount}</span>
                    <button className="card-btn card-btn-sm" disabled={trackerHistoryPage >= trackerHistoryPageCount} onClick={() => setTrackerHistoryPage((page) => Math.min(trackerHistoryPageCount, page + 1))} type="button">Volgende</button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'system' && (
              <div className="admin-system-section">
                <div className="admin-section-heading"><div><span className="admin-section-kicker">Diagnose</span><h2>Processen en verbindingen</h2><p>Controleer runs, verwerkingstijden en actieve liveverbindingen.</p></div></div>
                <div className="admin-panel-grid">
                  <div className="card">
                    <h3>Scheduler</h3>
                    {schedulerStatus ? (
                      <div className="admin-status-list">
                        <div className="admin-status-item"><span>Proces actief</span>{renderBooleanBadge(schedulerStatus.running, 'Ja', 'Nee')}</div>
                        <div className="admin-status-item"><span>Modus</span><strong>{schedulerStatus.mode}</strong></div>
                        <div className="admin-status-item"><span>Controle-interval</span><strong>{schedulerStatus.pollIntervalSeconds}s</strong></div>
                        <div className="admin-status-item"><span>Laatste run</span><strong>{formatDateTime(schedulerStatus.lastRunFinishedAt || schedulerStatus.lastRunAt)}</strong></div>
                        <div className="admin-status-item"><span>Run duur</span><strong>{formatDurationSeconds(schedulerStatus.lastRunDurationSeconds)}</strong></div>
                        <div className="admin-status-item"><span>Laatste resultaat</span><strong>{schedulerStatus.lastRunSummary.sent} verzonden / {schedulerStatus.lastRunSummary.errors} fouten</strong></div>
                      </div>
                    ) : (
                      <p>Scheduler status niet beschikbaar vanaf deze frontendconfiguratie.</p>
                    )}
                  </div>
                  <div className="card">
                    <h3>Live websocket worker</h3>
                    {liveWsStatus ? (
                      <div className="admin-status-list">
                        <div className="admin-status-item"><span>Proces actief</span>{renderBooleanBadge(liveWsStatus.running, 'Ja', 'Nee')}</div>
                        <div className="admin-status-item"><span>Sport</span><strong>{liveWsStatus.sport}</strong></div>
                        <div className="admin-status-item"><span>Bookmakers</span><strong>{liveWsStatus.bookmakers.join(', ') || '-'}</strong></div>
                        <div className="admin-status-item"><span>Laatste verwerking</span><strong>{formatDateTime(liveWsStatus.lastProcessFinishedAt || liveWsStatus.lastProcessAt)}</strong></div>
                        <div className="admin-status-item"><span>Verwerkingsduur</span><strong>{formatDurationSeconds(liveWsStatus.lastProcessDurationSeconds)}</strong></div>
                        <div className="admin-status-item"><span>Laatste resultaat</span><strong>{liveWsStatus.lastProcessSummary.sent} verzonden / {liveWsStatus.lastProcessSummary.errors} fouten</strong></div>
                      </div>
                    ) : (
                      <p>Live WS status niet beschikbaar vanaf deze frontendconfiguratie.</p>
                    )}
                  </div>
                </div>

                <div className="card">
                  <h3>Live verbindingen</h3>
                  {liveWsConnections.length === 0 ? (
                        <p>Geen details van liveverbindingen beschikbaar.</p>
                  ) : (
                    <div className="admin-connection-list">
                      {liveWsConnections.map(([bookmaker, connection]) => (
                        <div className="admin-status-item" key={bookmaker}>
                          <div>
                            <strong>{bookmaker}</strong>
                            <div className="admin-table-subtext">Frames: {connection.framesReceived} | Events: {connection.eventCount}</div>
                          </div>
                          <div>
                            {renderBooleanBadge(connection.connected, 'Verbonden', 'Offline')}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </section>
    </PublicLayout>
  );
};

export default AdminPage;
